
<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import MainLayout from '@/components/layout/MainLayout.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import PaymentStatCard from '@/components/payments/PaymentStatCard.vue'
import PaymentFilterBar from '@/components/payments/PaymentFilterBar.vue'
import PaymentTable from '@/components/payments/PaymentTable.vue'
import PaymentEditModal from '@/components/payments/PaymentEditModal.vue'
import { usePaymentStore } from '@/stores/payment'

const router = useRouter()
const paymentStore = usePaymentStore()
const paymentEditModal = ref(null)

const search = computed({
  get: () => paymentStore.filters.search,
  set: (value) => paymentStore.setSearch(value),
})

const method = computed({
  get: () => paymentStore.filters.method,
  set: (value) => paymentStore.setMethod(value),
})

const status = computed({
  get: () => paymentStore.filters.status,
  set: (value) => paymentStore.setStatus(value),
})

const formatCurrency = (value) => {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0,
  }).format(value || 0)
}

const handleCreatePayment = () => {
  router.push('/payments/create')
}

const handleViewPayment = (payment) => {
  if (!payment?.id) return
  router.push(`/payments/${payment.id}`)
}

</script>

<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <PageHeader
        title="รายการรับชำระเงิน"
        subtitle="จัดการการรับชำระค่าส่วนกลาง ตรวจสอบช่องทางชำระ และติดตามสถานะการบันทึกรายการ"
        button-text="ฟอร์มรับชำระเงิน"
        button-icon="bi-cash-coin"
        button-color="success"
        @action="handleCreatePayment"
      />

      <div class="row g-4 mb-4">
        <div class="col-md-6 col-xl-3">
          <PaymentStatCard
            title="รายการรับชำระทั้งหมด"
            :value="paymentStore.totalPayments.toLocaleString('th-TH')"
            subtitle="จำนวนรายการรับชำระในระบบ"
            icon="bi-cash-stack"
            color="primary"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <PaymentStatCard
            title="รับชำระสมบูรณ์"
            :value="paymentStore.completedPayments.toLocaleString('th-TH')"
            subtitle="รายการที่บันทึกเรียบร้อยแล้ว"
            icon="bi-check-circle"
            color="success"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <PaymentStatCard
            title="ชำระบางส่วน"
            :value="paymentStore.partialPayments.toLocaleString('th-TH')"
            subtitle="รายการที่ยังปิดยอดไม่ครบ"
            icon="bi-exclamation-circle"
            color="warning"
          />
        </div>

        <div class="col-md-6 col-xl-3">
          <PaymentStatCard
            title="ยอดรับชำระรวม"
            :value="formatCurrency(paymentStore.totalCollected)"
            subtitle="รวมยอดเงินที่รับชำระแล้ว"
            icon="bi-wallet2"
            color="info"
          />
        </div>
      </div>

      <PaymentFilterBar
        :search="search"
        :method="method"
        :status="status"
        :method-options="paymentStore.methodOptions"
        :status-options="paymentStore.statusOptions"
        @update:search="search = $event"
        @update:method="method = $event"
        @update:status="status = $event"
        @reset="paymentStore.resetFilters()"
      />

      <div class="row g-4 mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm rounded-4 bg-success bg-opacity-10">
            <div class="card-body p-4">
              <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                <div>
                  <div class="fw-bold fs-5 mb-1">รายการที่รอตรวจสอบหลักฐานการชำระ</div>
                  <div class="text-muted">ใช้ติดตามรายการที่บันทึกไว้แต่ยังรอยืนยันความถูกต้อง</div>
                </div>

                <div class="text-lg-end">
                  <div class="display-6 fw-bold text-success-emphasis mb-0">
                    {{ paymentStore.pendingVerifyPayments.toLocaleString('th-TH') }} รายการ
                  </div>
                  <div class="text-muted small">สถานะรอตรวจสอบ</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaymentTable
        :items="paymentStore.filteredPayments"
        @view="handleViewPayment"
        @edit="(p) => paymentEditModal.open(p)"
      />
      <PaymentEditModal ref="paymentEditModal" />
    </div>
  </MainLayout>
</template>
